import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import ListActions from 'Components/ListActions'
import PageTitle from 'Components/PageTitle'
import PTAFormField from 'Components/PTAFormField'
import { WithLoader } from 'Components/WithLoader'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import ZoneDefinitionDetails from 'Services/ProductManagement/Features/ZoneDefinition/Components/ZoneDefinitionDetails'
import ZoneDefinitionList from 'Services/ProductManagement/Features/ZoneDefinition/Components/ZoneDefinitionList'
import { useZoneDefinitionsWithParams } from 'Services/ProductManagement/Features/ZoneDefinition/Hooks'
import {
  useDeleteSingleZoneDefinitionMutation,
  useGetSingleZoneDefinitionQuery
} from 'Services/ProductManagement/Features/ZoneDefinition/zoneDefinitionApi'
import useTypedSelector from 'Store'
import {
  IS_WALTTI_SERVICE_ENVIRONMENT,
  PRODUCT_MANAGEMENT_ROOT_URL,
  ROWS_PER_PAGE_DEFAULT
} from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import useListFunctions from 'Util/hooks/useListFunctions'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

export default function ZoneDefinitionListPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/settings/zone-definitions`

  const navigate = useNavigate()
  const { confirm } = useConfirm()
  const { user } = useTypedSelector(state => state.user)
  const { setSelectedAuthorityCode } = useAuthorityContext()

  const breadcrumbs = [
    {
      title: t`Settings`
    },
    {
      title: t`Zone definitions`,
      link: baseUrl
    }
  ]

  const {
    zoneDefinitions,
    isFetching,
    searchQueryParams,
    setSearchQueryParams,
    total_results,
    selectedZoneDefinitionId,
    setSelectedZoneDefinitionId
  } = useZoneDefinitionsWithParams()

  const { currentData: singleZoneDefinition, isFetching: isSingleZoneDefinitionFetching } =
    useGetSingleZoneDefinitionQuery(selectedZoneDefinitionId ?? skipToken)

  const [deleteSingleZoneDefinition, { isLoading: isDeleteLoading }] =
    useDeleteSingleZoneDefinitionMutation()

  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedZoneDefinitionId)

  // List page can be reached without prior authority; reset so PTABanner reflects no selection
  // biome-ignore lint/correctness/useExhaustiveDependencies: suppress setSelectedAuthorityCode
  useEffect(() => {
    setSelectedAuthorityCode(undefined)
  }, [])

  const { handleChangePage, handleRequestSort, handlePTAChange } = useListFunctions({
    searchQueryParams,
    setSearchQueryParams
  })

  const handleDrawerClose = () => {
    setSelectedZoneDefinitionId(undefined)
    setIsDrawerOpen(false)
  }

  const handleRowClick = (id: string) => {
    setSelectedZoneDefinitionId(id)
    setIsDrawerOpen(true)
  }

  const handleDelete = () => {
    if (!selectedZoneDefinitionId) return
    deleteSingleZoneDefinition(selectedZoneDefinitionId)
      .unwrap()
      .then(() => {
        setSelectedZoneDefinitionId(undefined)
        setIsDrawerOpen(false)
        SnackbarUtils.success(t`Zone definition successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`An error occurred while deleting the zone definition.`)
      })
  }

  const isLoading = isFetching || isDeleteLoading
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <PageTitle>{t`Zone definitions`}</PageTitle>
      </Box>
      <ListActions>
        {isSuperuser(user) && (
          <CustomButton
            onClick={() => navigate(`${baseUrl}/create`)}
            size="small"
            startIcon={<AddIcon />}
          >
            {t`Create new zone definition`}
          </CustomButton>
        )}
        {IS_WALTTI_SERVICE_ENVIRONMENT && (
          <PTAFormField
            value={searchQueryParams.pta ?? undefined}
            onChange={handlePTAChange}
            required={false}
            displayEmptyFirstOption={true}
          />
        )}
      </ListActions>
      <Box pt={1}>
        <ZoneDefinitionList
          rows={zoneDefinitions}
          isLoading={isLoading}
          params={searchQueryParams}
          handleRequestSort={handleRequestSort}
          handleChangePage={handleChangePage}
          count={total_results}
          page={
            searchQueryParams.start_index
              ? Number.parseInt(searchQueryParams.start_index, 10) / ROWS_PER_PAGE_DEFAULT
              : 0
          }
          onRowClick={handleRowClick}
          selectedZoneDefinitionId={selectedZoneDefinitionId}
        />
      </Box>
      <DrawerRight
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        topBar={
          <Box>
            {isSuperuser(user) && (
              <>
                <DeleteButton
                  sx={{ mr: 1 }}
                  size="small"
                  onClick={() =>
                    confirm({
                      title: t`Are you sure?`,
                      message: t`Are you sure you want to delete the zone definition?`,
                      handleConfirm: () => {
                        handleDelete()
                      }
                    })
                  }
                >{t`Delete`}</DeleteButton>
                <CustomButton
                  onClick={() => {
                    setSelectedAuthorityCode(singleZoneDefinition?.pta)
                    navigate(`${baseUrl}/${selectedZoneDefinitionId}/edit`)
                  }}
                  size="small"
                  startIcon={<EditIcon />}
                >{t`Edit`}</CustomButton>
              </>
            )}
          </Box>
        }
      >
        <WithLoader loading={isSingleZoneDefinitionFetching || isDeleteLoading}>
          {singleZoneDefinition && (
            <TabContext value={'details'}>
              <TabList aria-label={t`Details`}>
                <Tab label={t`Details`} value={'details'} />
              </TabList>
              <Divider />
              <TabPanel sx={{ p: 0 }} value={'details'}>
                <ZoneDefinitionDetails zoneDefinition={singleZoneDefinition} />
              </TabPanel>
            </TabContext>
          )}
        </WithLoader>
      </DrawerRight>
    </Box>
  )
}
